/**
 * Reading a CSV that has real text in it.
 *
 * A chart note is paragraphs - "Subjective\nFeeling run down…" - so a note
 * exported from Boulevard carries newlines INSIDE a quoted cell. Splitting the
 * file into lines first and parsing each one, which is fine for a list of
 * names, tears every note into pieces at exactly the point the nurse pressed
 * return. This walks the file character by character instead, so a quoted cell
 * can contain commas, quotes and line breaks and still arrive whole.
 */

export interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
}

/**
 * What separates one cell from the next.
 *
 * "the area in blvd can you open it up to paste please asap much easier then
 *  download upload"
 *
 * Copying rows straight out of a web table or a spreadsheet puts TABS between
 * the cells, not commas - so a pasted Boulevard table read as a comma file is
 * one enormous column. This looks at the first line and takes whichever
 * separator is actually being used, outside quotes.
 */
function sniffDelimiter(src: string): string {
  const firstLine = src.split(/\r?\n/, 1)[0] ?? "";
  let tabs = 0, commas = 0, semis = 0, inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (inQuotes) continue;
    if (ch === "\t") tabs++;
    else if (ch === ",") commas++;
    else if (ch === ";") semis++;
  }
  if (tabs > commas && tabs >= semis) return "\t";
  if (semis > commas && semis > tabs) return ";";
  return ",";
}

export function parseCsv(text: string): ParsedCsv {
  const src = text.replace(/^﻿/, "");   // Excel's byte-order mark
  const delim = sniffDelimiter(src);
  const records: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;

  const endCell = () => { row.push(cell); cell = ""; };
  const endRow = () => {
    endCell();
    // A trailing newline at the end of the file is not an empty record.
    if (row.length > 1 || row[0] !== "") records.push(row);
    row = [];
  };

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }   // "" is a literal quote
        else inQuotes = false;
      } else {
        cell += ch;                                      // including newlines
      }
      continue;
    }
    if (ch === '"') { inQuotes = true; continue; }
    if (ch === delim) { endCell(); continue; }
    if (ch === "\r") { if (src[i + 1] === "\n") i++; endRow(); continue; }
    if (ch === "\n") { endRow(); continue; }
    cell += ch;
  }
  if (cell !== "" || row.length > 0) endRow();

  const headerRow = records.shift() ?? [];
  const headers = headerRow.map((h) => h.trim());
  const rows = records.map((cells) => {
    const out: Record<string, string> = {};
    headers.forEach((h, idx) => { out[h] = (cells[idx] ?? "").trim(); });
    return out;
  });
  return { headers, rows };
}
